import { useQuery } from '@tanstack/react-query';
import { History, ArrowRight, Truck } from 'lucide-react';
import { api } from '@/api/client';

type FieldChange = { field: string; label: string; before: any; after: any };

type EditLog = {
  id: string;
  orderId: string;
  userId?: number | null;
  actorName?: string | null;
  actorEmpCode?: string | null;
  actorType: string;
  reason?: string | null;
  changes: FieldChange[];
  statusId?: number | null;
  isPostShip: boolean;
  ip?: string | null;
  createdAt: string;
};

/**
 * Render a logged value for reading, not for round-tripping.
 *
 * `before`/`after` are stored raw, so a change to Products or Sub-Orders arrives as an array of
 * objects. Dumping that JSON into the row would bury the one-line address correction that sits
 * beside it, so structured values collapse to a count and the detail moves to the hover title.
 */
function renderValue(v: any): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? '' : 's'}`;
  if (typeof v === 'object') {
    const parts = Object.entries(v)
      .filter(([, x]) => x !== null && x !== undefined && x !== '')
      .map(([k, x]) => `${k}: ${x}`);
    return parts.length ? parts.join(', ') : '—';
  }
  return String(v);
}

function fullValue(v: any): string {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'object') { try { return JSON.stringify(v, null, 2); } catch { return String(v); } }
  return String(v);
}

/**
 * Who changed what on this order, from what, to what, and why.
 *
 * **Internal staff only — never render this for a merchant identity.** It carries our own people's
 * names, emp codes and their stated reasons for a correction. The API refuses merchant callers on
 * `/orders/:id/edit-logs` as well, so this is the second of two guards, not the only one.
 */
export function OrderEditHistory({ orderId }: { orderId: string | number }) {
  const { data: logs = [], isLoading, error } = useQuery({
    queryKey: ['order-edit-logs', String(orderId)],
    queryFn: async () => (await api.get<EditLog[]>(`/orders/${orderId}/edit-logs`)).data,
  });

  // A staff user without Orders view permission gets a 403 here. That is not an error worth shouting
  // about on an order page they are already looking at — just show nothing.
  if (error) return null;

  const rows = logs as EditLog[];

  return (
    <div className="border rounded-lg bg-white p-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <History size={16} className="text-indigo-700" />
        <h3 className="font-semibold text-slate-900">Edit History</h3>
        <span className="text-xs text-gray-400">({rows.length} {rows.length === 1 ? 'edit' : 'edits'})</span>
        <span className="ml-auto text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Internal only — not visible to merchants</span>
      </div>

      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-gray-400 text-sm">This order has never been edited.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((l) => (
            <div key={l.id} className="rounded border border-slate-100 bg-slate-50 p-2.5">
              <div className="flex items-center flex-wrap gap-2 text-xs text-gray-500">
                <span className="font-medium text-slate-700">{l.actorName ?? (l.userId ? `User #${l.userId}` : 'System')}</span>
                {l.actorEmpCode && <span className="text-gray-400">· {l.actorEmpCode}</span>}
                {l.actorType === 'merchant' && <span className="bg-slate-200 text-slate-600 px-1.5 rounded text-[10px]">Merchant</span>}
                {l.isPostShip && (
                  <span className="bg-amber-100 text-amber-700 px-1.5 rounded text-[10px] inline-flex items-center gap-1"
                    title="Edited after the shipment was booked, on the Gaint network">
                    <Truck size={9} /> After booking
                  </span>
                )}
                <span className="ml-auto">{new Date(l.createdAt).toLocaleString()}</span>
              </div>

              {l.reason && (
                <div className="text-sm text-slate-800 mt-1.5">
                  <span className="text-slate-500 text-xs">Reason: </span>{l.reason}
                </div>
              )}

              <div className="mt-1.5 space-y-1">
                {(l.changes ?? []).map((c, i) => (
                  <div key={`${l.id}-${c.field}-${i}`} className="text-xs flex items-start gap-2 flex-wrap">
                    <span className="text-slate-500 min-w-[120px]">{c.label}</span>
                    <span className="text-rose-700 line-through break-all" title={fullValue(c.before)}>{renderValue(c.before)}</span>
                    <ArrowRight size={11} className="text-slate-400 mt-0.5 shrink-0" />
                    <span className="text-emerald-700 font-medium break-all" title={fullValue(c.after)}>{renderValue(c.after)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
